
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const alertVariants = cva(
  "relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground",
  {
    variants: {
      variant: {
        default: "border-[#E5E7EB] bg-[#FCFCFD] text-[#6B7280]",
        destructive:
          "border-[#FEE2E2] bg-[#FEFBFB] text-[#DC2626] dark:border-[#FEE2E2] [&>svg]:text-[#DC2626]",
        warning:
          "border-[#FED7AA] bg-[#FFFCFA] text-[#F99539] dark:text-[#F99539] dark:border-[#FED7AA] [&>svg]:text-[#F99539]",
        success:
          "border-[#BBF7D0] bg-[#FBFFFA] text-[#2FBF12] dark:text-[#2FBF12] dark:border-[#BBF7D0] [&>svg]:text-[#2FBF12]",
        info:
          "border-[#BFDBFE] bg-[#FAFBFF] text-[#396CF9] dark:text-[#396CF9] dark:bg-[#FAFBFF] [&>svg]:text-[#396CF9]",
        tip:
          "border-[#E5E4C8] bg-[#FBFBF9] text-[#9F9E5F] dark:text-[#9F9E5F] dark:bg-[#FBFBF9] [&>svg]:text-[#9F9E5F]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
))
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
))
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription }
